import React, { useEffect } from 'react';
import { ImageInput } from '../../../../components/image-input';
import { LabeledField } from '../../../../components/form-fields/labeled-field';
import { Section } from '../../../../components/form-fields/section';
import { TextField } from '../../../../components/form-fields/text-field';
import { BackgroundColorPicker } from '../../../../components/background-color-picker';
import { BACKGROUND_COLORS } from '../../../../components/background-color-picker/constants';
import { RichText } from '../../../../components/rich-text';
import type { PromotionForm } from '../../../../stores/promotion';
import {
    localizeLanguage,
    localizeLanguageShort,
} from '../../../../utils/languages';
import { PreviewValidationHelper } from './PreviewValidationHelper';
import style from './style.module.scss';

const SUGGESTED_TITLE_LENGTH = 18;
const SUGGESTED_SUBTITLE_LENGTH = 28;
const MAX_LENGTH = 255;

interface Offer {
    id: number;
    items: {
        title?: string;
        language: string;
    }[];
}

interface OfferDetailsSectionProps {
    values: PromotionForm;
    setFieldValue: (field: string, value: unknown) => void;
    offerStore: {
        offers: { results: Offer[] } | null;
        isFetchingOffers: boolean;
        getOffersById: (id: number) => Offer | undefined;
        fetchOffers: () => Promise<void>;
        offersMap: Map<number, Offer>;
    };
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const OfferDetailsSection = ({
    values,
    setFieldValue,
    offerStore,
    handleChange,
    handleBlur,
}: OfferDetailsSectionProps) => {
    useEffect(() => {
        offerStore.fetchOffers();
    }, [offerStore]);

    return (
        <Section title="Content">
            <PreviewValidationHelper
                hasPreview={values.hasPreview}
                items={values.items}
            />
            <div className="columns_layout">
                {values.items.map((item, i) => {
                    const localizedLanguage = localizeLanguage(item.language);
                    const localizedLanguageShort = localizeLanguageShort(
                        item.language
                    );
                    return (
                        <div key={`${item.language}-${i}`} className="column">
                            <span
                                className="h5_bold"
                                style={{ marginBottom: 0 }}
                            >
                                {localizedLanguage}
                            </span>

                            <div className={style.image_uploaders}>
                                <LabeledField
                                    label={`Featured image (${localizedLanguageShort})`}
                                >
                                    <ImageInput
                                        url={item.thumbnail}
                                        onChange={(url) =>
                                            setFieldValue(
                                                `items[${i}].thumbnail`,
                                                url
                                            )
                                        }
                                        onDelete={() =>
                                            setFieldValue(
                                                `items[${i}].thumbnail`,
                                                null
                                            )
                                        }
                                    />
                                </LabeledField>
                                {!item.backgroundColor && (
                                    <LabeledField
                                        label={`Background (${localizedLanguageShort})`}
                                    >
                                        <ImageInput
                                            url={item.background}
                                            onChange={(url) =>
                                                setFieldValue(
                                                    `items[${i}].background`,
                                                    url
                                                )
                                            }
                                            onDelete={() =>
                                                setFieldValue(
                                                    `items[${i}].background`,
                                                    null
                                                )
                                            }
                                        />
                                    </LabeledField>
                                )}
                                {values.hasPreview && (
                                    <LabeledField label="Preview Image *">
                                        <ImageInput
                                            url={item.previewImageUrl || null}
                                            onChange={(url) =>
                                                setFieldValue(
                                                    `items[${i}].previewImageUrl`,
                                                    url
                                                )
                                            }
                                            onDelete={() =>
                                                setFieldValue(
                                                    `items[${i}].previewImageUrl`,
                                                    null
                                                )
                                            }
                                        />
                                    </LabeledField>
                                )}
                            </div>

                            <div>
                                <BackgroundColorPicker
                                    value={item.backgroundColor}
                                    onChange={(value) => {
                                        setFieldValue(
                                            `items[${i}].backgroundColor`,
                                            value
                                        );
                                        // Only set text color for promoted banners
                                        if (values.isPromotedBanner) {
                                            if (value !== null) {
                                                const colorOption = BACKGROUND_COLORS.find(
                                                    (c) => c.value === value
                                                );
                                                setFieldValue(
                                                    `items[${i}].promotedBannerTextColor`,
                                                    colorOption?.titleColor ??
                                                        null
                                                );
                                            } else {
                                                setFieldValue(
                                                    `items[${i}].promotedBannerTextColor`,
                                                    null
                                                );
                                            }
                                        }
                                        // Clear background image when color is set
                                        if (value !== null) {
                                            setFieldValue(
                                                `items[${i}].background`,
                                                null
                                            );
                                        }
                                    }}
                                    language={item.language}
                                />
                            </div>

                            <LabeledField
                                label={`Title (${localizedLanguage})`}
                            >
                                <TextField
                                    name={`items[${i}].title`}
                                    maxLength={MAX_LENGTH}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={item.title}
                                    suggestedLength={SUGGESTED_TITLE_LENGTH}
                                />
                            </LabeledField>

                            <LabeledField
                                label={`Subtitle (${localizedLanguage})`}
                            >
                                <TextField
                                    name={`items[${i}].subTitle`}
                                    maxLength={MAX_LENGTH}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={item.subTitle}
                                    suggestedLength={SUGGESTED_SUBTITLE_LENGTH}
                                />
                            </LabeledField>

                            {values.hasPreview && (
                                <LabeledField label="Preview Title">
                                    <TextField
                                        name={`items[${i}].previewTitle`}
                                        maxLength={MAX_LENGTH}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={item.previewTitle || ''}
                                        suggestedLength={SUGGESTED_TITLE_LENGTH}
                                    />
                                </LabeledField>
                            )}

                            <LabeledField
                                label={`Details page description (${localizedLanguage})`}
                            >
                                <RichText
                                    text={item.description}
                                    onChange={(text) =>
                                        setFieldValue(
                                            `items[${i}].description`,
                                            text
                                        )
                                    }
                                />
                            </LabeledField>
                        </div>
                    );
                })}
            </div>
        </Section>
    );
};
